import React, { useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import StatusBadge from '../../../components/Badges/StatusBadge';
import AlumlyBreadCrumb, { PathT } from '../../../components/AlumlyBreadCrumb';
import AlumlyButton from '../../../components/AlumlyButton';
import { PermissionEnum } from '../../../enums/permission';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import usePermission from '../../../hooks/usePermission';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import DeleteNewsModal from '../components/DeleteNewsModal';
import FormNews, { NewsFormData } from '../components/FormNews';

const EditNews: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkPermission } = usePermission();
  const [formData, setFormData] = useState<NewsFormData | null>(null);
  const [isPublished, setIsPublished] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { service: getDetailService } = useServices(ServiceNameEnum.news.detail);

  const getDetail = async (): Promise<void> => {
    const params = { id };
    const { data, error } = await getDetailService.get({ params });
    if (data) {
      setIsPublished(data.isPublished ?? false);
      setFormData({
        titleTh: data.titleML?.th || '',
        titleEn: data.titleML?.en || '',
        descriptionTh: data.descriptionML?.th || '',
        descriptionEn: data.descriptionML?.en || '',
        linkUrl: data.linkUrl || '',
        isPublished: data.isPublished ?? false,
        coverImages: data.coverFile ? [{
          key: `cover-${data.coverFile.id}`,
          id: data.coverFile.id,
          type: data.coverFile.fileType || 'image/jpeg',
          name: data.coverFile.fileName || 'cover',
          size: 0,
          preview: data.coverFile.presignUrl || '',
          dimension: { width: 0, height: 0 },
        }] : [],
      });
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
      navigate(RouteNameEnum.news.index);
    }
  };

  useEffect(() => { getDetail(); }, []);

  const path: PathT[] = [
    { label: 'News', value: RouteNameEnum.news.index },
    { label: 'Edit news' },
  ];

  return (
    <div>
      <AlumlyBreadCrumb path={path} />
      <Typography.Title level={4} style={{ fontWeight: 'bold' }}>Edit News</Typography.Title>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
          <Typography.Text type="secondary">status</Typography.Text>
          <StatusBadge status={isPublished} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
          {checkPermission([PermissionEnum.code.newsDelete]) && (
            <AlumlyButton label="rxvWhite" icon={<DeleteIcon />} width={200} onClick={() => setOpenDeleteModal(true)}>
              Delete
            </AlumlyButton>
          )}
        </div>
      </div>
      <DeleteNewsModal newsId={Number(id)} isOpen={openDeleteModal} handleClose={() => setOpenDeleteModal(false)} />
      {formData && <FormNews mode="edit" newsId={id} defaultValues={formData} />}
    </div>
  );
};

export default EditNews;
